<template>
  <div class="fg-pax-form" role="group" :aria-label="typeLabel">
    <div class="fg-pax-form__head">
      <span class="fg-pax-form__title">{{ typeLabel }}</span>
      <span v-if="bandLabel" class="fg-pax-form__band">{{ bandLabel }}</span>
    </div>

    <div class="fg-form fg-pax-form__body">
      <label class="fg-field">
        <span class="fg-field__label">
          {{ t('booking.firstName') }}<span class="fg-field__req" aria-hidden="true">*</span>
        </span>
        <input
          :value="passenger.first_name"
          type="text"
          required
          :aria-invalid="errors.first_name || undefined"
          :class="{ 'is-invalid': errors.first_name }"
          @input="emitPatch('first_name', $event)"
          @blur="touched.first_name = true"
        />
        <span v-if="errors.first_name" class="fg-field__error">{{ t('booking.required') }}</span>
      </label>

      <label class="fg-field">
        <span class="fg-field__label">
          {{ t('booking.lastName') }}<span class="fg-field__req" aria-hidden="true">*</span>
        </span>
        <input
          :value="passenger.last_name"
          type="text"
          required
          :aria-invalid="errors.last_name || undefined"
          :class="{ 'is-invalid': errors.last_name }"
          @input="emitPatch('last_name', $event)"
          @blur="touched.last_name = true"
        />
        <span v-if="errors.last_name" class="fg-field__error">{{ t('booking.required') }}</span>
      </label>

      <label class="fg-field">
        <span class="fg-field__label">
          {{ t('booking.gender') }}<span class="fg-field__req" aria-hidden="true">*</span>
        </span>
        <select
          :value="passenger.gender"
          required
          @change="emitPatch('gender', $event)"
        >
          <option value="Male">{{ t('booking.genderMale') }}</option>
          <option value="Female">{{ t('booking.genderFemale') }}</option>
        </select>
      </label>

      <label class="fg-field">
        <span class="fg-field__label">
          {{ t('booking.birthDate') }}<span class="fg-field__req" aria-hidden="true">*</span>
        </span>
        <input
          :value="passenger.birth_date"
          type="date"
          required
          :min="birthDateBounds?.min"
          :max="birthDateBounds?.max"
          :aria-invalid="errors.birth_date || Boolean(ageError) || undefined"
          :class="{ 'is-invalid': errors.birth_date || Boolean(ageError) }"
          @change="emitPatch('birth_date', $event)"
          @blur="touched.birth_date = true"
        />
        <span v-if="errors.birth_date" class="fg-field__error">{{ t('booking.required') }}</span>
        <span v-else-if="ageErrorMessage" class="fg-field__error">{{ ageErrorMessage }}</span>
      </label>

      <label class="fg-field">
        <span class="fg-field__label">
          {{ t('booking.passportNumber') }}<span class="fg-field__req" aria-hidden="true">*</span>
        </span>
        <input
          :value="passenger.doc_number"
          type="text"
          required
          autocomplete="off"
          :aria-invalid="errors.doc_number || undefined"
          :class="{ 'is-invalid': errors.doc_number }"
          @input="emitPatch('doc_number', $event)"
          @blur="touched.doc_number = true"
        />
        <span v-if="errors.doc_number" class="fg-field__error">{{ t('booking.required') }}</span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { useI18n } from '../../i18n'
import type { Passenger } from '../../../../shared/types'
import type { AgeBand } from '../../../../shared/rules'

const props = defineProps<{
  passenger: Passenger
  /** Tranche d'âge du transporteur pour ce type — `null` si non configurée. */
  ageBand?: AgeBand | null
  /** Bornes `YYYY-MM-DD` de l'input date, dérivées de la bande et des départs. */
  birthDateBounds?: { min: string, max: string } | null
  /** Écart détecté entre la date de naissance et le type déclaré. */
  ageError?: 'FUTURE' | 'OUT_OF_BAND' | null
}>()
const emit = defineEmits<{ update: [patch: Partial<Passenger>] }>()

const { t } = useI18n()

// En-tête = TYPE traduit ("Adulte" / "Enfant" / "Bébé") + âge pour enfants/bébés
// ("Enfant · 8 ans"). On n'affiche plus « Passager N » (l'order global est
// trompeur dans un dossier groupé).
const TYPE_I18N: Record<string, string> = {
  Adult: 'passengers.adult',
  Child: 'passengers.child',
  Baby: 'passengers.baby',
}
const typeLabel = computed(() => {
  const p = props.passenger
  const base = t(TYPE_I18N[String(p.type)] ?? 'passengers.adult')
  return (p.type === 'Child' || p.type === 'Baby') && p.age != null
    ? `${base} · ${p.age} ${t('passengers.years')}`
    : base
})

// Rappel de la tranche admissible à côté du titre — « 1 à 13 ans au départ ».
// La borne haute de l'adulte (120) n'est pas affichée : seul le seuil d'entrée
// est informatif.
const bandLabel = computed(() => {
  const band = props.ageBand
  if (!band) return ''
  return props.passenger.type === 'Adult'
    ? t('booking.ageBandAdult', { min: band.min })
    : t('booking.ageBand', { min: band.min, max: band.max })
})

// Message d'écart type ↔ date de naissance. L'âge s'évalue à la date de départ
// de chaque leg — un passager doit rester dans sa bande à l'aller ET au retour.
const ageErrorMessage = computed(() => {
  if (props.ageError === 'FUTURE') return t('booking.birthDateAfterDeparture')
  if (props.ageError !== 'OUT_OF_BAND' || !props.ageBand) return ''
  return t('booking.ageOutOfBand', {
    type: t(TYPE_I18N[String(props.passenger.type)] ?? 'passengers.adult').toLowerCase(),
    min: props.ageBand.min,
    max: props.ageBand.max,
  })
})

// Suivi du blur par champ — on n'affiche l'erreur que si le user a déjà
// touché le champ ET qu'il est vide à l'instant courant. Évite les "erreurs
// criardes" dès l'arrivée sur la page.
const touched = reactive({
  first_name: false,
  last_name: false,
  birth_date: false,
  doc_number: false,
})

const errors = computed(() => ({
  first_name: touched.first_name && !isNonEmpty(props.passenger.first_name),
  last_name: touched.last_name && !isNonEmpty(props.passenger.last_name),
  birth_date: touched.birth_date && !isNonEmpty(props.passenger.birth_date),
  doc_number: touched.doc_number && !isNonEmpty(props.passenger.doc_number),
}))

function isNonEmpty(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0
}

function emitPatch(field: keyof Passenger, event: Event): void {
  const target = event.target as HTMLInputElement | HTMLSelectElement
  emit('update', { [field]: target.value } as Partial<Passenger>)
}
</script>
